import { Button, Form, Input, message } from 'antd';
import React, { FunctionComponent, useState } from 'react';
import {
  useAutoCompleteSearchStudentLazyQuery,
  useChangeUsernameMutation,
  useFilterUserQuery,
} from '@services/data-access';

import { FormItemProps } from 'antd/lib/form';
import Modal from './ChangeUsernamePopup.styled';
import { StatusCode } from '@models';
import debounce from 'lodash/debounce';
import { userNameValidator } from '@helpers';

export interface ChangeUsernamePopupProps {
  userId: string;
  visible: boolean;
  onClose: () => void;
  role?: string;
  handleUnChange?: (value: boolean) => void;
  onSubmitPopUp?: (value: object) => void;
}

export const ChangeUsernamePopup: FunctionComponent<
  ChangeUsernamePopupProps
> = ({ userId, visible, onClose, role, handleUnChange, onSubmitPopUp }) => {
  const [form] = Form.useForm();
  const [validateUsername, setValidateUsername] = useState<
    FormItemProps['validateStatus'] | undefined
  >(undefined);
  const { data: userDetail } = useFilterUserQuery({
    skip: !Boolean(userId),
    variables: {
      filter: {
        id: {
          _eq: userId,
        },
      },
    },
  });
  const [getUserByUsername, { loading: validateUsernameLoading }] =
    useAutoCompleteSearchStudentLazyQuery({
      onCompleted: result => {
        const username = form.getFieldValue('username');
        setValidateUsername(
          result.users.length > 0
            ? 'error'
            : username.trim() !== ''
            ? 'success'
            : undefined,
        );
      },
      onError: () => {},
    });
  const getUserByUsernameDebounce = debounce(getUserByUsername, 300);

  const [changeUsernameMutation, { loading }] = useChangeUsernameMutation({
    onCompleted: () => {
      const { username } = form.getFieldsValue(['username']);
      if (handleUnChange) {
        handleUnChange(true);
      }
      if (onSubmitPopUp) {
        onSubmitPopUp({ username });
      }
      message.success('Changed username successfully.');
      closePopup();
    },
    onError: () => {
      closePopup();
    },
  });
  const handleChangeUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim() === '') {
      // setValidateUsername(undefined);

      return;
    }
    getUserByUsernameDebounce({
      variables: {
        form: {
          username: {
            _eq: e.target.value.trim(),
          },
          status: {
            _neq: StatusCode.Deleted,
          },
        },
      },
    });
  };

  const handleSubmit = async (data: any) => {
    form
      .validateFields()
      .then(() => {
        if (validateUsername === 'success') {
          const { username } = form.getFieldsValue(['username']);
          changeUsernameMutation({
            variables: {
              form: {
                username: username.trim(),
                id: userId ? userId : '',
              },
            },
          });
        }
      })
      .catch(() => {
        closePopup();
      });
  };
  const resetForm = () => {
    form.resetFields();
  };

  const closePopup = () => {
    resetForm();
    onClose();
    if (handleUnChange) {
      handleUnChange(false);
    }
  };

  return (
    <Modal
      forceRender
      centered
      title="Change Username"
      visible={visible}
      onCancel={closePopup}
      className="modal-change-name"
      footer={[
        <Button
          className="fixed-width-btn button-black-text"
          key="back"
          onClick={closePopup}
        >
          Cancel
        </Button>,
        <Button
          loading={loading}
          className="fixed-width-btn"
          key="submit"
          type="primary"
          onClick={() => {
            form
              .validateFields()
              .then(values => handleSubmit(values))
              .catch(info => {});
          }}
        >
          Save
        </Button>,
      ]}
    >
      <Form onFinish={handleSubmit} form={form} layout="vertical">
        <Form.Item
          label="Username"
          name="username"
          rules={[
            {
              required: true,
              message: 'Please fill in your new username!',
            },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (
                  value &&
                  !userId &&
                  userDetail?.users[0]?.username === value
                ) {
                  return Promise.reject(
                    'New username must be different with Old username!',
                  );
                }
                return Promise.resolve();
              },
            }),
            {
              validator: userNameValidator,
            },
          ]}
          // help={
          //   validateUsername === 'success'
          //     ? 'Available to use'
          //     : validateUsername === 'error'
          //     ? 'Could not use this username!'
          //     : undefined
          // }
          hasFeedback
          validateStatus={
            validateUsernameLoading ? 'validating' : validateUsername
          }
        >
          <Input
            onChange={handleChangeUsername}
            placeholder="Fill in your new username"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
